import React, {useContext, useEffect, useState} from "react";
import CategoriesGrid from "../../../../components/category/CategoriesGrid";
import styled from "styled-components";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import {dehydrate, QueryClient, useQuery} from '@tanstack/react-query';
import {useRouter} from "next/router";
import LoadMoreProductListing from "../../../../components/product/LoadMoreProductListing";
import {useCountryLang} from "../../../../hooks/useCountryLang";
import TitleArea from "../../../../components/TitleArea";
import {UrlAttrFilterQueryContext} from "../../../../contexts/UrlAttrFilterQueryContext";
import {request} from "../../../../shared/httpInterceptor";
import {ITopCategory} from "../../../../interfaces/ITopCategory";
import {ICategory} from "../../../../interfaces/ICategory";
import PageHead from "../../../../components/PageHead";
import {getHost, withServerSideProps} from "../../../../shared/withServerSideProps";
import {useAtom} from "jotai";
import {categoryDescriptionAtom} from "../../../../store/categoryDescriptionAtom";
import ShowMore from "../../../../components/utils/ShowMore";
import parse from "html-react-parser";
import {getTheme} from "../../../../shared/themeUtils";
import WebBanner from "../../../../components/WebBanner";
import {IWebBanner} from "../../../../interfaces/IWebBanner";
import NotFound from "../../../../components/NotFound";
import {extractIdFromSSR, extractIdFromUrl} from "../../../../shared/utils";
import {NotFoundType} from "../../../../shared/enums";

const PAGE_SIZE = 30;

const fetchCurrentCategory = async (categoryId: number | null, countryLang: string | null, host?: string, context?: any) => {
  const requestConfig: any = {
    url: `/${countryLang}/category/${categoryId}`,
    method: 'get',
    host: host
  };

  // Only add context and cookies if they exist
  if (context?.req?.cookies) {
    requestConfig.cookies = context.req.cookies;
  }
  if (context) {
    requestConfig.context = context;
  }

  const res = await request(requestConfig);
  return res.data;
}


const fetchSubcategories = async (categoryId: number | null, countryLang: string | null, host?: string, context?: any) => {
  const requestConfig: any = {
    url: `/${countryLang}/category/tree/${categoryId}`,
    method: 'get',
    host: host
  };

  // Only add context and cookies if they exist
  if (context?.req?.cookies) {
    requestConfig.cookies = context.req.cookies;
  }
  if (context) {
    requestConfig.context = context;
  }

  const res = await request(requestConfig);
  return res.data;
}


const fetchBreadcrumbs = async (categoryId: number | null, countryLang: string | null, host?: string, context?: any) => {
  const requestConfig: any = {
    url: `/${countryLang}/category/breadcrumbs/${categoryId}`,
    method: 'get',
    host: host
  };

  // Only add context and cookies if they exist
  if (context?.req?.cookies) {
    requestConfig.cookies = context.req.cookies;
  }
  if (context) {
    requestConfig.context = context;
  }

  const res = await request(requestConfig);
  return res.data;
}

const fetchCategoryBanner = async (countryLang: string | null, categoryId: number | null, host?: string, cookies?: any) => {
  const res = await request({
    url: `${countryLang}/banner/category/${categoryId}?orientationCode=horizontal`,
    method: 'get',
    host: host,
    cookies: cookies
  })
  return res.data;
}

const CategoryContain = styled.div`
    margin: ${getTheme("space.2")} auto;

    h1 {
        margin-bottom: 0;
    }

    .content-text {
        white-space: pre-wrap;
    }
`

type CategoryPropsType = {
  category: ICategory,
  subcategories: ITopCategory[],
}

const Category = (props: CategoryPropsType) => {
  const router = useRouter()
  const countryLang = useCountryLang()
  // @ts-ignore
  const splitRoute = router.query.slug.toString().split("-");
  const [currentCategoryId, setCurrentCategoryId] = useState<number | null>(null)
  const [serverStateId, setServerStateId] = useState("")
  const {urlFilterQueryCtx} = useContext(UrlAttrFilterQueryContext)
  const [expectedListingCountRequestUrl, setExpectedListingCountRequestUrl] = useState(`listing/category/${currentCategoryId}`)
  const [categoryDescription] = useAtom(categoryDescriptionAtom)

  const fetchProducts = async ({pageParam = 0}) => {
    const paramsArr = [];
    paramsArr.push(`size=${PAGE_SIZE}`)
    paramsArr.push(`offset=${pageParam}`)
    if (router.query.sort) {
      paramsArr.push(`sort=${router.query.sort.toString()}`)
    }
    if (router.query.search) {
      paramsArr.push(`search=${router.query.search.toString()}`)
    }
    if (urlFilterQueryCtx) {
      paramsArr.push(`filter=${urlFilterQueryCtx}`)
    }
    if (serverStateId) {
      paramsArr.push(`state=${serverStateId}`)
    }
    const res = await request({
      url: `/${countryLang}/listing/category/${currentCategoryId}?${paramsArr.join('&')}`,
      method: 'get'
    });
    // state param is used server-side for caching
    // we pass it back with every subsequent request
    if (res.data && res.data.state) {
      setServerStateId(res.data.state)
    }
    return res.data;
  }

  useEffect(() => {
    if (!router.isReady) return;

    const id = extractIdFromUrl(router);
    setCurrentCategoryId(id);
    
    // Only update the expected listing URL if we have a valid ID
    if (id) {
      setExpectedListingCountRequestUrl(`listing/category/${id}`);
    }
  }, [router.isReady, router.asPath]);

  // Add a check to prevent API calls with null category ID
  const currentCategoryQuery = useQuery(["current-category", currentCategoryId, countryLang],
    () => fetchCurrentCategory(currentCategoryId, countryLang), {
      enabled: countryLang != null && currentCategoryId != null,
      initialData: props.category
    });

  const subcategoriesQuery = useQuery(["subcategories", currentCategoryId, countryLang],
    () => fetchSubcategories(currentCategoryId, countryLang), {
      enabled: countryLang != null && currentCategoryId != null,
      initialData: props.subcategories
    });

  const breadcrumbsQuery = useQuery(["category-breadcrumbs", currentCategoryId, countryLang],
    () => fetchBreadcrumbs(currentCategoryId, countryLang), {
      enabled: countryLang != null && currentCategoryId != null
    });

  const categoryBannerQuery = useQuery<IWebBanner>(["category-banner", currentCategoryId],
    () => fetchCategoryBanner(countryLang, currentCategoryId), {
      enabled: currentCategoryId != null && countryLang != null,
    });

  // Not Found Layout
  if (currentCategoryQuery.data && currentCategoryQuery.data.errorID) {
    return (
      <NotFound type={NotFoundType.CATEGORY}/>
    )
  }

  return (
    <CategoryContain className="appContainer">
      <PageHead
        currentPageTitle={currentCategoryQuery.data?.category.name}
        canonical={typeof window !== 'undefined' ? window.location.href : ''}
        openGraph={{
          title: currentCategoryQuery.data?.category.name
        }}
      />

      {breadcrumbsQuery.data &&
        <Breadcrumbs items={breadcrumbsQuery.data}/>
      }

      {currentCategoryQuery.data &&
        <TitleArea title={currentCategoryQuery.data.category.name}/>
      }

      {categoryBannerQuery.data &&
        <WebBanner banner={categoryBannerQuery.data}/>
      }

      {categoryDescription &&
        <div className={"my-3"}>
          <ShowMore>
            <div className="content-text">
              {parse(categoryDescription)}
            </div>
          </ShowMore>
        </div>
      }

      {(subcategoriesQuery.data && subcategoriesQuery.data.length > 0) &&
        <CategoriesGrid categories={subcategoriesQuery.data}/>}


      {countryLang != null && currentCategoryId != null &&
        <LoadMoreProductListing
          queryKeyArray={["category-products", currentCategoryId]}
          fetchFunction={fetchProducts}
          allowShowEmptyListing={!(subcategoriesQuery.data && subcategoriesQuery.data.length > 0)}
          expectedListingCountRequestUrl={expectedListingCountRequestUrl}/>
      }

    </CategoryContain>
  )
}


export const getServerSideProps = withServerSideProps(async (context: any) => {
  const queryClient = new QueryClient();
  const {params} = context;
  const currentCategoryId = extractIdFromSSR(params)
  const countryLang = `${context.query.country}/${context.query.lang}`
  const host = getHost(context.req);

  let category;
  let subcategories;

  if (currentCategoryId && !isNaN(currentCategoryId)) {
    await Promise.allSettled([
      category = await fetchCurrentCategory(currentCategoryId, countryLang, host, context),
      subcategories = await fetchSubcategories(currentCategoryId, countryLang, host, context),
    ])
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      category,
      subcategories,
    }
  }
})

export default Category;
